import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ChainStep, Pipeline } from '../types';

interface NodePos { x: number; y: number; }

interface VisualChainBuilderProps {
  pipelines: Pipeline[];
  value: ChainStep[];
  onChange: (steps: ChainStep[]) => void;
  readOnly?: boolean;
  style?: React.CSSProperties;
  customContainers?: Array<{container_id: string; name: string}>;
}

// A lightweight, dependency-free visual DAG builder for chains.
// - Add pipelines from the left palette to the canvas.
// - Drag nodes to reposition.
// - Toggle "Connect" then click source node and target node to create an edge (target depends on source).
// - Click a node to select; use small controls to clear its dependencies or remove from canvas.
// - No external libraries to keep footprint minimal.
export default function VisualChainBuilder({ pipelines, value, onChange, readOnly, style, customContainers }: VisualChainBuilderProps) {
  const [positions, setPositions] = useState<Record<string, NodePos>>({});
  const [presentIds, setPresentIds] = useState<string[]>([]); // nodes on canvas
  const [connectMode, setConnectMode] = useState<boolean>(false);
  const [connectSource, setConnectSource] = useState<string | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const canvasRef = useRef<HTMLDivElement | null>(null);
  
  // Get selected step
  const selectedStep = useMemo(() => value.find(s => s.pipeline_id === selected), [value, selected]);

  // Build quick maps
  const pipelineName = useMemo(() => Object.fromEntries(pipelines.map(p => [p.pipeline_id, p.name])), [pipelines]);
  const stepsMap = useMemo(() => {
    const m: Record<string, string[]> = {};
    (value || []).forEach(s => { m[s.pipeline_id] = [...(s.depends_on || [])]; });
    return m;
  }, [value]);

  // Ensure presentIds is aligned with value
  useEffect(() => {
    const ids = (value || []).map(s => s.pipeline_id);
    setPresentIds(ids);
    // initialize positions if absent
    setPositions(prev => {
      const next: Record<string, NodePos> = { ...prev };
      let i = 0;
      ids.forEach(id => {
        if (!next[id]) {
          next[id] = { x: 120 + (i % 4) * 180, y: 80 + Math.floor(i / 4) * 140 };
          i += 1;
        }
      });
      // drop removed ids
      Object.keys(next).forEach(k => { if (!ids.includes(k)) delete next[k]; });
      return next;
    });
  }, [value]);

  const canvasSize = { width: 1200, height: 600 };

  const addNode = (pid: string) => {
    if (readOnly) return;
    if (presentIds.includes(pid)) return;
    const nextSteps: ChainStep[] = [...value, { pipeline_id: pid, depends_on: [] }];
    onChange(nextSteps);
  };

  const removeNode = (pid: string) => {
    if (readOnly) return;
    // Remove the node and references from other depends_on
    const filtered = value.filter(s => s.pipeline_id !== pid).map(s => ({
      ...s,
      depends_on: (s.depends_on || []).filter(d => d !== pid)
    }));
    onChange(filtered);
    setSelected(null);
  };

  const clearDeps = (pid: string) => {
    if (readOnly) return;
    const next = value.map(s => s.pipeline_id === pid ? { ...s, depends_on: [] } : s);
    onChange(next);
  };

  const updateRequiresSpark = (pid: string, requires: boolean) => {
    if (readOnly) return;
    const next = value.map(s => s.pipeline_id === pid ? { ...s, requires_spark: requires } : s);
    onChange(next);
  };

  const updateRequiresContainer = (pid: string, containerId: string | null) => {
    if (readOnly) return;
    const next = value.map(s => s.pipeline_id === pid ? { ...s, requires_container: containerId } : s);
    onChange(next);
  };

  const startConnect = () => {
    if (readOnly) return;
    setConnectMode(!connectMode);
    setConnectSource(null);
  };

  const handleConnectClick = (pid: string) => {
    if (!connectMode || readOnly) return;
    if (!connectSource) {
      setConnectSource(pid);
      return;
    }
    if (connectSource === pid) {
      // same node; cancel
      setConnectSource(null);
      return;
    }
    // connect: target depends on source
    const source = connectSource;
    const target = pid;
    // Prevent self-dependency and duplicate
    const next = value.map(s => {
      if (s.pipeline_id !== target) return s;
      const deps = new Set([...(s.depends_on || [])]);
      deps.add(source);
      return { ...s, depends_on: Array.from(deps) };
    });

    // cycle detection (simple DFS)
    const graph: Record<string, string[]> = {};
    next.forEach(s => { graph[s.pipeline_id] = [...(s.depends_on || [])]; });
    const visit: Record<string, number> = {};
    const dfs = (u: string): boolean => {
      const st = visit[u] || 0;
      if (st === 1) return true; // cycle
      if (st === 2) return false;
      visit[u] = 1;
      for (const v of graph[u] || []) {
        if (dfs(v)) return true;
      }
      visit[u] = 2;
      return false;
    };
    let hasCycle = false;
    Object.keys(graph).forEach(n => { if (!hasCycle && dfs(n)) hasCycle = true; });
    if (hasCycle) {
      alert('Cannot create this link: it would create a cycle.');
    } else {
      onChange(next);
    }
    setConnectSource(null);
  };

  // Drag logic
  const dragInfo = useRef<{ id: string; dx: number; dy: number } | null>(null);
  const onMouseDownNode = (e: React.MouseEvent, id: string) => {
    if (readOnly) return;
    const rect = (canvasRef.current as HTMLDivElement).getBoundingClientRect();
    const pos = positions[id] || { x: 0, y: 0 };
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    dragInfo.current = { id, dx: mouseX - pos.x, dy: mouseY - pos.y };
    setSelected(id);
  };
  const onMouseMove = (e: React.MouseEvent) => {
    if (!dragInfo.current || readOnly) return;
    const rect = (canvasRef.current as HTMLDivElement).getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const { id, dx, dy } = dragInfo.current;
    const x = Math.max(20, Math.min(canvasSize.width - 160, mouseX - dx));
    const y = Math.max(20, Math.min(canvasSize.height - 60, mouseY - dy));
    setPositions(prev => ({ ...prev, [id]: { x, y } }));
  };
  const onMouseUp = () => { dragInfo.current = null; };

  const edges = useMemo(() => {
    const es: { from: string; to: string }[] = [];
    presentIds.forEach(target => {
      (stepsMap[target] || []).forEach(dep => es.push({ from: dep, to: target }));
    });
    return es;
  }, [presentIds, stepsMap]);

  const allAvailable = useMemo(() => pipelines.filter(p => !presentIds.includes(p.pipeline_id)), [pipelines, presentIds]);

  const containerStyle: React.CSSProperties = {
    display: 'grid',
    gridTemplateColumns: '320px 1fr',
    gap: 16,
    ...(style || {})
  };

  return (
    <div style={containerStyle}>
      {/* Palette */}
      <div style={{ background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, height: canvasSize.height, display: 'flex', flexDirection: 'column' }}>
        <div style={{ fontWeight: 700, marginBottom: 12, fontSize: 15 }}>Available Pipelines</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, flex: 1, overflow: 'auto', marginBottom: 12 }}>
          {allAvailable.map(p => (
            <button key={p.pipeline_id} disabled={readOnly} onClick={() => addNode(p.pipeline_id)} style={{
              textAlign: 'left',
              whiteSpace: 'normal',
              wordWrap: 'break-word',
              border: '1px solid #e5e7eb', 
              background: '#f9fafb', 
              padding: '10px 12px', 
              borderRadius: 8,
              fontSize: 14,
              lineHeight: 1.4,
              cursor: readOnly ? 'default' : 'pointer',
              transition: 'all 0.15s ease'
            }}
            onMouseEnter={(e) => !readOnly && (e.currentTarget.style.background = '#f3f4f6')}
            onMouseLeave={(e) => (e.currentTarget.style.background = '#f9fafb')}
            >{p.name}</button>
          ))}
          {allAvailable.length === 0 && <div style={{ color: '#6b7280', fontSize: 13, padding: '8px 0' }}>All pipelines have been added to the chain.</div>}
        </div>
        {!readOnly && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
            <button onClick={startConnect} style={{ 
              border: '1px solid #e5e7eb', 
              padding: '10px 12px', 
              borderRadius: 8, 
              background: connectMode ? '#ecfeff' : 'white', 
              color: connectMode ? '#0369a1' : '#374151',
              fontWeight: 600,
              fontSize: 14
            }}>
              {connectMode ? (connectSource ? 'Select target…' : 'Click source node') : 'Connect Mode'}
            </button>
            {selected && selectedStep && (
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => clearDeps(selected)} style={{ 
                  flex: 1,
                  border: '1px solid #e5e7eb', 
                  padding: '8px 10px', 
                  borderRadius: 8, 
                  background: 'white',
                  fontSize: 13
                }}>Clear Deps</button>
                <button onClick={() => removeNode(selected)} style={{ 
                  flex: 1,
                  border: '1px solid #fee2e2', 
                  padding: '8px 10px', 
                  borderRadius: 8, 
                  background: '#fef2f2', 
                  color: '#991b1b',
                  fontSize: 13
                }}>Remove</button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Canvas */}
      <div
        ref={canvasRef}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        style={{ position: 'relative', background: '#ffffff', border: '1px solid #e5e7eb', borderRadius: 8, height: canvasSize.height }}
      >
        {/* Edges via SVG */}
        <svg width={canvasSize.width} height={canvasSize.height} style={{ position: 'absolute', inset: 0 }}>
          {edges.map((e, idx) => {
            const a = positions[e.from] || { x: 0, y: 0 };
            const b = positions[e.to] || { x: 0, y: 0 };
            const ax = a.x + 140; const ay = a.y + 24; // right middle of source
            const bx = b.x; const by = b.y + 24; // left middle of target
            const mx = (ax + bx) / 2;
            return (
              <g key={idx}>
                <path d={`M ${ax} ${ay} C ${mx} ${ay}, ${mx} ${by}, ${bx} ${by}`} stroke="#60a5fa" strokeWidth={2} fill="none" />
                {/* arrow head */}
                <polygon points={`${bx},${by} ${bx-6},${by-4} ${bx-6},${by+4}`} fill="#60a5fa" />
              </g>
            );
          })}
        </svg>

        {/* Nodes */}
        {presentIds.map(pid => {
          const pos = positions[pid] || { x: 40, y: 40 };
          const isSel = selected === pid;
          const isConnectSel = connectSource === pid;
          const label = pipelineName[pid] || pid;
          return (
            <div
              key={pid}
              onMouseDown={(e) => onMouseDownNode(e, pid)}
              onClick={() => { if (connectMode) { handleConnectClick(pid); } else { setSelected(pid); } }}
              style={{
                position: 'absolute', left: pos.x, top: pos.y, width: 140, height: 48,
                border: `2px solid ${isSel ? '#111827' : isConnectSel ? '#0369a1' : '#e5e7eb'}`,
                borderRadius: 8, background: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: readOnly ? 'default' : 'move', userSelect: 'none', boxShadow: isSel ? '0 4px 8px rgba(0,0,0,0.08)' : 'none'
              }}
              title={pid}
            >
              <div style={{ padding: '0 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontWeight: 600 }}>{label}</div>
            </div>
          );
        })}

        {/* Floating Prerequisites Panel */}
        {!readOnly && selected && selectedStep && (() => {
          const pos = positions[selected] || { x: 40, y: 40 };
          // Position panel to the right of the node with some offset
          const panelX = Math.min(pos.x + 160, canvasSize.width - 280);
          const panelY = Math.max(20, Math.min(pos.y, canvasSize.height - 260));
          
          return (
            <div style={{
              position: 'absolute',
              left: panelX,
              top: panelY,
              width: 260,
              padding: 16,
              background: 'white',
              border: '2px solid #111827',
              borderRadius: 10,
              boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
              zIndex: 100
            }}>
              <div style={{ 
                fontWeight: 700, 
                marginBottom: 12, 
                fontSize: 14,
                color: '#111827',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}>
                <span>Prerequisites</span>
                <button
                  onClick={() => setSelected(null)}
                  style={{
                    background: 'none',
                    border: 'none',
                    fontSize: 18,
                    cursor: 'pointer',
                    padding: 0,
                    color: '#6b7280',
                    lineHeight: 1
                  }}
                  title="Close"
                >×</button>
              </div>
              
              <label style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 8, 
                marginBottom: 12, 
                fontSize: 14,
                cursor: 'pointer'
              }}>
                <input
                  type="checkbox"
                  checked={selectedStep.requires_spark !== false}
                  onChange={(e) => updateRequiresSpark(selected, e.target.checked)}
                  style={{ cursor: 'pointer' }}
                />
                <span>Requires Spark Cluster</span>
              </label>
              
              <div style={{ fontSize: 13, marginBottom: 6, fontWeight: 600, color: '#374151' }}>
                Custom Container:
              </div>
              <select
                value={selectedStep.requires_container || ''}
                onChange={(e) => updateRequiresContainer(selected, e.target.value || null)}
                style={{ 
                  width: '100%', 
                  padding: '8px 10px', 
                  borderRadius: 6, 
                  border: '1px solid #d1d5db', 
                  fontSize: 14,
                  background: 'white'
                }}
              >
                <option value="">None</option>
                {(customContainers || []).map(c => (
                  <option key={c.container_id} value={c.container_id}>{c.name}</option>
                ))}
              </select>
              
              <div style={{ 
                marginTop: 12, 
                paddingTop: 12, 
                borderTop: '1px solid #e5e7eb',
                fontSize: 12,
                color: '#6b7280'
              }}>
                Pipeline: <strong style={{ color: '#111827' }}>{pipelineName[selected]}</strong>
              </div>
            </div>
          );
        })()}

        {/* Legend / help */}
        <div style={{ position: 'absolute', left: 12, bottom: 12, color: '#6b7280', fontSize: 12, background: 'rgba(255,255,255,0.9)', padding: '6px 10px', borderRadius: 6 }}>
          {readOnly ? 'View mode — editing disabled' : 'Click a node to configure prerequisites • Toggle Connect Mode to link nodes'}
        </div>
      </div>
    </div>
  );
}
